import React, { useEffect, useState, useCallback } from "react";
import useAuthFetch from "../../hooks/useAuthFetch";
import Select from "../Select";
import EconomicActivitySelect from "../EconomicActivitySelect";
import { debounce } from "lodash";
import { validateIdentification, formatIdentification } from "../../utils/identificationValidation";

export function BillingForm({ billing, onChangeValue, onChangeAll }) {
  const [idTypes, setIdTypes] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [emails, setEmails] = useState([""]);
  const [identificationError, setIdentificationError] = useState("");
  const authFetch = useAuthFetch();

  const fetchIdTypes = useCallback(async () => {
    const response = await authFetch(
      'catalogos/7?filter={"include":[{"relation":"catalogos","scope":{"offset":0,"limit":100,"skip":0}}]}'
    );
    const data = await response.json();
    setIdTypes(data.catalogos);
  }, [authFetch]);

  useEffect(() => {
    fetchIdTypes();
  }, [fetchIdTypes]);

  // Sincronizar emails cuando se carga billing data
  useEffect(() => {
    if (billing.correoFactura) {
      const emailsArray = billing.correoFactura.split(',').map(email => email.trim()).filter(email => email);
      setEmails(emailsArray.length > 0 ? emailsArray : [""]);
    }
  }, [billing.correoFactura]);





  const fetchBilling = async (newIdentification, showFeedback = false) => {
    if (showFeedback) {
      setIsSearching(true);
      setSearchMessage("");
    }

    try {
      const fetchBilling = await authFetch(
        `usuario-facturacion/buscar-por-identificacion/${encodeURIComponent(newIdentification)}`
      );

      if (fetchBilling.status === 200) {
        const data = await fetchBilling.json();
        if (data) {
          // Procesar los emails separados por comas
          if (data.correoFactura) {
            const emailsArray = data.correoFactura.split(',').map(email => email.trim()).filter(email => email);
            setEmails(emailsArray.length > 0 ? emailsArray : [""]);
          }

          onChangeAll(data);
          if (showFeedback) {
            const emailCount = data.correoFactura ? data.correoFactura.split(',').filter(email => email.trim()).length : 0;
            if (emailCount > 1) {
              setSearchMessage(`Datos encontrados: ${emailCount} correos cargados correctamente`);
            } else {
              setSearchMessage("Datos encontrados y cargados correctamente");
            }
          }
        } else {
          if (showFeedback) {
            setSearchMessage("No se encontraron datos para esta identificación");
          }
        }
      } else {
        if (showFeedback) {
          setSearchMessage("No se encontraron datos para esta identificación");
        }
      }
    } catch (error) {
      console.error('Error al buscar datos de facturación:', error);
      if (showFeedback) {
        setSearchMessage("Error al buscar los datos. Intenta nuevamente");
      }
    } finally {
      if (showFeedback) {
        setIsSearching(false);
        setTimeout(() => setSearchMessage(""), 3000);
      }
    }
  }

  const debouncedFetchBilling = debounce(fetchBilling, 1000);

  // Función para validar la identificación
  const validateCurrentIdentification = (identification, type) => {
    if (!identification || !type) {
      setIdentificationError("");
      return;
    }

    const validation = validateIdentification(identification, parseInt(type));
    setIdentificationError(validation.isValid ? "" : validation.message);
  };

  // Funciones para manejar múltiples emails
  const addEmailField = () => {
    if (emails.length < 4) {
      setEmails([...emails, ""]);
    }
  };

  const removeEmailField = (index) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
      updateCombinedEmails(newEmails);
    }
  };

  const updateEmail = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
    updateCombinedEmails(newEmails);
  };

  const updateCombinedEmails = (emailsArray) => {
    const validEmails = emailsArray.filter(email => email.trim() !== "");
    const combinedEmails = validEmails.join(", ");
    onChangeValue("correoFactura", combinedEmails);
  };



  return (
    <div>
      <div>
        <Select
          containerClasses="my-2"
          labelColor="text-base"
          buttonClasses="border border-purple"
          label="Tipo de identificación:"
          options={idTypes.map((type) => {
            return {
              value: type.idEnCatalogo,
              label: type.valor,
            };
          })}
          onChange={(value) => {
            onChangeValue("tipoIdentificacion", value);
            // Revalidar la identificación con el nuevo tipo
            if (billing.identificacion) {
              validateCurrentIdentification(billing.identificacion, value);
            }
          }}
          value={billing.tipoIdentificacion}
        />
      </div>
      <div>
        <label htmlFor="identificacion" className="text-base">
          Identificación:
        </label>
        <div className="flex gap-2 my-2">
          <input
            id="identificacion"
            name="identificacion"
            className={`flex-1 rounded-3xl h-12 p-6 border ${identificationError ? 'border-red-500' : 'border-purple'}`}
            value={billing.identificacion}
            onChange={(evt) => {
              const newValue = evt.target.value;
              onChangeValue("identificacion", newValue);

              // Validar la identificación
              validateCurrentIdentification(newValue, billing.tipoIdentificacion);

              // Buscar datos si es válida y tiene longitud suficiente
              if (newValue && newValue.length > 5) {
                debouncedFetchBilling(newValue);
              }
            }}
            onBlur={(evt) => {
              // Formatear la identificación al perder el foco
              if (evt.target.value && billing.tipoIdentificacion) {
                const formatted = formatIdentification(evt.target.value, parseInt(billing.tipoIdentificacion));
                if (formatted !== evt.target.value) {
                  onChangeValue("identificacion", formatted);
                }
              }
            }}
          />
          <button
            type="button"
            onClick={() => {
              if (billing.identificacion && billing.identificacion.length > 5) {
                fetchBilling(billing.identificacion, true);
              }
            }}
            className="bg-cyan hover:bg-cyan/80 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!billing.identificacion || billing.identificacion.length <= 5 || isSearching}
          >
            {isSearching ? "Verificando..." : "Verificar"}
          </button>
        </div>
        {identificationError && (
          <p className="text-pink text-sm mt-1 font-medium">
            {identificationError}
          </p>
        )}
        {searchMessage && (
          <p className={`text-sm mt-1 ${searchMessage.includes("encontrados") ? "text-green-600" :
            searchMessage.includes("Error") ? "text-red-600" : "text-yellow-600"
            }`}>
            {searchMessage}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="nombreFactura" className="text-base">
          Nombre completo:
        </label>
        <input
          id="nombreFactura"
          name="nombreFactura"
          className="w-full rounded-3xl my-2 h-12 p-6 border border-purple"
          value={billing.nombreFactura}
          onChange={(evt) => {
            onChangeValue("nombreFactura", evt.target.value);
          }}
        />
      </div>
      <div>
        <EconomicActivitySelect
          value={billing.actividadEconomica}
          onChange={(value) => {
            onChangeValue("actividadEconomica", value);
          }}
          label="Actividad económica:"
          placeholder="Buscar por código o descripción..."
          className="my-2"
        />
      </div>


      <div>
        <label htmlFor="correoFactura" className="text-base mb-2 block">
          Correo(s) para enviar factura:
        </label>

        <div className="space-y-3">
          {emails.map((email, index) => (
            <div key={index} className="flex gap-2 items-center">
              <input
                type="email"
                className="flex-1 rounded-3xl h-12 p-6 border border-purple"
                value={email}
                onChange={(evt) => updateEmail(index, evt.target.value)}
                placeholder={`Correo ${index + 1}`}
              />
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={addEmailField}
                  className="bg-cyan hover:bg-cyan/80 text-white px-3 py-2 rounded-full font-bold transition-colors duration-200 flex items-center justify-center disabled:opacity-30"
                  title="Agregar otro correo"
                  disabled={emails.length >= 4}
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => removeEmailField(index)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full font-black transition-colors duration-200 flex items-center justify-center shadow-lg border-2 border-red-800"
                  title="Eliminar este correo"
                  style={{
                    backgroundColor: '#dc2626',
                    minWidth: '40px',
                    minHeight: '40px',
                    fontSize: '18px',
                    boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.3)',
                    opacity: emails.length <= 1 ? 0.4 : 1,
                    cursor: emails.length <= 1 ? 'not-allowed' : 'pointer'
                  }}
                  disabled={emails.length <= 1}
                >
                  <span style={{ fontSize: '20px', fontWeight: '900' }}>×</span>
                </button>
              </div>
            </div>
          ))}

          {/* Botones principales cuando solo hay un correo */}
          {emails.length === 1 && (
            <div className="flex justify-center gap-3">
              {emails.length < 4 && (
                <button
                  type="button"
                  onClick={addEmailField}
                  className="bg-cyan hover:bg-cyan/80 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Agregar otro correo ({emails.length}/4)
                </button>
              )}
            </div>
          )}

          {/* Botones de acción cuando hay múltiples correos */}
          {emails.length > 1 && (
            <div className="flex justify-center gap-3">
              {emails.length < 4 && (
                <button
                  type="button"
                  onClick={addEmailField}
                  className="bg-cyan hover:bg-cyan/80 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Agregar correo ({emails.length}/4)
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  // Eliminar el último correo vacío o el último correo
                  const lastEmptyIndex = emails.findLastIndex(email => !email.trim());
                  const indexToRemove = lastEmptyIndex !== -1 ? lastEmptyIndex : emails.length - 1;
                  removeEmailField(indexToRemove);
                }}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 flex items-center gap-2"
              >
                <span className="text-lg">×</span>
                Eliminar último
              </button>
            </div>
          )}

          {emails.filter(email => email.trim()).length > 1 && (
            <div className="mt-3 p-3 bg-cyan/10 border-l-4 border-cyan rounded-lg">
              <p className="text-sm text-gray-700">
                <strong className="text-cyan-700">📧 Correos que recibirán la factura:</strong>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {emails.filter(email => email.trim()).join(" • ")}
              </p>
            </div>
          )}
        </div>

        {/* Campo de dirección para facturación electrónica */}
        <div className="mb-6">
          <label htmlFor="direccionLucida" className="text-base">
            Dirección para factura electrónica: <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="direccionLucida"
            name="direccionLucida"
            placeholder="Ingrese la dirección que aparecerá en la factura electrónica"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-transparent transition-all duration-200"
            onChange={(evt) => {
              onChangeValue("direccionLucida", evt.target.value);
            }}
            value={billing.direccionLucida || ""}
          />
          <p className="text-sm text-gray-500 mt-1">
            Esta dirección aparecerá en su factura electrónica y será enviada a Hacienda
          </p>
        </div>
      </div>
    </div>
  );
}

export default BillingForm;
